"use client";

// Auto-logout pri nečinnosti používateľa.
// Adaptované z dev/components/InactivityDetector.tsx s týmito úpravami:
//   - Volá náš logout Server Action (server-side redirect, nie window.location)
//   - Odstránený nepoužitý useRouter import
//   - Defaults zachované 55/60 (warning/logout v minútach), pre erp default 1h
//
// Komponent renderuje IBA dialog (skoro celý čas neviditeľný). Logika beží
// na pozadí cez useEffect timery + window event listenery na user activity.

import { useEffect, useState, useRef } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { logout } from "@/app/login/actions";

interface InactivityDetectorProps {
  // Čas nečinnosti v minútach, po ktorom sa zobrazí varovanie pred odhlásením.
  warningTime?: number;
  // Celkový čas nečinnosti v minútach po ktorom sa user reálne odhlási.
  // logoutTime - warningTime = ako dlho má dialog odpočítavať.
  logoutTime?: number;
}

export default function InactivityDetector({
  warningTime = 55,
  logoutTime = 60,
}: InactivityDetectorProps) {
  const [showWarning, setShowWarning] = useState(false);
  const [countdown, setCountdown] = useState(0);

  // Ref na aktuálnu hodnotu showWarning - bez tohto by event listenery v
  // resetTimer videli hodnotu z času ich registrácie (closure capture problem).
  const showWarningRef = useRef(false);
  useEffect(() => {
    showWarningRef.current = showWarning;
  }, [showWarning]);

  useEffect(() => {
    let inactivityTimer: NodeJS.Timeout;
    let warningTimer: NodeJS.Timeout;
    let countdownInterval: NodeJS.Timeout;

    // Vyčistí všetky bežiace timery + zruší dialog ak je otvorený.
    // Volaná pri každom user evente a pri unmount-e komponenta.
    const resetTimer = () => {
      clearTimeout(inactivityTimer);
      clearTimeout(warningTimer);
      clearInterval(countdownInterval);

      if (showWarningRef.current) {
        setShowWarning(false);
      }

      // Po `warningTime` minútach nečinnosti zobrazíme varovanie.
      warningTimer = setTimeout(
        () => {
          setShowWarning(true);
          // Koľko sekúnd ešte ostáva do skutočného logoutu - to ukážeme
          // v dialógu a každú sekundu dekrementujeme.
          const remainingSeconds = Math.floor((logoutTime - warningTime) * 60);
          setCountdown(remainingSeconds);

          countdownInterval = setInterval(() => {
            setCountdown((prev) => {
              if (prev <= 1) {
                clearInterval(countdownInterval);
                return 0;
              }
              return prev - 1;
            });
          }, 1000);
        },
        warningTime * 60 * 1000,
      );

      // Po `logoutTime` minútach reálne odhlásime usera.
      // logout Server Action sama spraví server-side redirect na /login.
      inactivityTimer = setTimeout(
        () => {
          logout().catch((err) => {
            // logout() throws redirect Error - to NIE JE chyba, je to ako Next.js
            // implementuje redirect-y. Logujeme iba "skutočné" chyby.
            if (err?.message && !err.message.includes("NEXT_REDIRECT")) {
              console.error("ERR #INACTIVITY: logout zlyhal:", err);
            }
          });
        },
        logoutTime * 60 * 1000,
      );
    };

    resetTimer();

    // Eventy ktoré považujeme za "user je tu, neodhlasovať".
    // touchstart kvôli touch zariadeniam (PDA, tablety).
    const events = [
      "mousedown",
      "mousemove",
      "keypress",
      "scroll",
      "touchstart",
    ];

    events.forEach((event) => {
      window.addEventListener(event, resetTimer);
    });

    return () => {
      clearTimeout(inactivityTimer);
      clearTimeout(warningTimer);
      clearInterval(countdownInterval);
      events.forEach((event) => {
        window.removeEventListener(event, resetTimer);
      });
    };
  }, [warningTime, logoutTime]);

  // Formát "M:SS" pre countdown v dialógu.
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
  };

  return (
    <Dialog open={showWarning} onOpenChange={setShowWarning}>
      <DialogContent>
        <div className="py-4">
          <p className="text-center">
            Automatické odhlásenie pri nečinnosti o{" "}
            <span className="font-bold">{formatTime(countdown)}</span>
          </p>
          <p className="text-center text-sm text-gray-500 mt-2">
            Pohnite myšou alebo stlačte klávesu, aby ste reláciu predĺžili.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
